<template>
  <v-container fluid class="pa-0">
    <EntityIndex
      :loading="loading"
      :total-count="totalCount"
      :table-headers="tableHeaders"
      :table-rows="tableRows"
      :table-options.sync="tableOptions"
      :selected-rows.sync="selectedRows"
      :selected-rows-actions="selectedRowsActions"
      :no-data-text="$t('service.noServicesMatchingCriteria')"
      item-key="service.id">
      <template v-slot:actions>
        <v-btn
          v-if="$featurePermissions.partQuickUseServiceCreate"
          color="primary"
          elevation="2"
          class="mb-2"
          @click="openQuickPartUseServiceDialog">
          {{ $t('service.quickPartUseServiceDialog.dialogTitle') }}
        </v-btn>
      </template>

      <template v-slot:filter>
        <entity-view-filter>
          <template v-slot:filter1>
            <date-range-picker
              v-model="dateRange"
              :start-date-clearable="true"
              :end-date-clearable="true"
              @change="fetch"/>
          </template>
          <template v-slot:filter2>
            <device-select-field
              v-model="filterDevice"
              :label="$t('device.singular')"
              @change="fetch"
              :show-select-dialog-button="true"/>
          </template>
          <template v-slot:filter3>
            <user-select-field
              v-model="filterDoneByUser"
              :label="$t('service.doneByUser')"
              @change="fetch"
              :show-select-dialog-button="true"/>
          </template>
          <template v-slot:filter4>
            <search-field
              v-model="filterText"
              hide-details="auto"
              :label="$t('service.filterByText')"
              @change="fetch"/>
          </template>
        </entity-view-filter>
      </template>

      <template v-slot:item.doneDate="{ item }">
        <router-link :to="item.routerLinkTo">{{ item.doneDate }}</router-link>
      </template>

      <template v-slot:item.doneByUsers="{ item }">
        <plain-unordered-list :items="item.doneByUsers" />
      </template>

      <template v-slot:entityListItem="props">
        <service-list-item
          :item="props.item"
          :show-actions="true"
          :row-click-enabled="true"/>
      </template>
    </EntityIndex>

    <entity-confirm-delete-dialog
      ref="confirmDeleteDialog"
      :text="$t('service.deleteConfirm')"
      :text-plural="$t('service.deleteConfirmPlural')"
      @confirmedDelete="deleteServices">
      <template v-slot:entityListItem="props">
        <service-list-item :item="props.item" />
      </template>
    </entity-confirm-delete-dialog>

    <service-dialog ref="serviceDialog" @done="fetch" />

    <quick-part-use-service-dialog
      ref="quickPartUseServiceDialog"
      @done="fetch"
    />
  </v-container>
</template>

<style scoped></style>

<script>
import EntityViewFilter from '@/components/entity-crud/EntityViewFilter';
import DateRangePicker from '@/components/inputs/DateRangePicker';
import SearchField from '@/components/inputs/SearchField';
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import DeviceSelectField from '@/components/inputs/DeviceSelectField';
import UserSelectField from '@/components/inputs/UserSelectField';
import ServiceListItem from '@/views/service/ServiceListItem';
import ServiceService from '@/services/ServiceService';
import QuickPartUseServiceDialog from '@/views/service/QuickPartUseServiceDialog';
import PlainUnorderedList from '@/components/layouts/PlainUnorderedList';
import ServiceDialog from '@/views/service/ServiceDialog';

export default {
  components: {
    ServiceDialog,
    PlainUnorderedList,
    QuickPartUseServiceDialog,
    ServiceListItem,
    UserSelectField,
    DeviceSelectField,
    EntityConfirmDeleteDialog,
    EntityIndex,
    SearchField,
    EntityViewFilter,
    DateRangePicker,
  },
  name: 'ServiceIndex',
  data() {
    return {
      services: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.$store.state.serviceIndexState.dateRange;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setDateRange', value);
      },
    },
    filterText: {
      get() {
        return this.$store.state.serviceIndexState.filterText;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setFilterText', value);
      },
    },
    filterDevice: {
      get() {
        return this.$store.state.serviceIndexState.filterDevice;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setFilterDevice', value);
      },
    },
    tableOptions: {
      get() {
        return this.$store.state.serviceIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setTableOptions', value);
      },
    },
    filterDoneByUser: {
      get() {
        return this.$store.state.serviceIndexState.filterAuthorUser;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setFilterDoneByUser', value);
      },
    },
    filterStatusChoices: {
      get() {
        return this.$store.state.serviceIndexState.filterStatusChoices;
      },
      set(value) {
        this.$store.commit('serviceIndexState/setFilterStatusChoices', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'locationTop',
          text: i18n.t('location.locationTop'),
          getValue: (service) => {
            return service.locationTop;
          },
        },
        {
          value: 'locationSub',
          text: i18n.t('location.locationSub'),
          getValue: (service) => {
            return service.locationSub;
          },
        },
        {
          value: 'doneDate',
          text: i18n.t('service.doneDate'),
          getValue: (service) => {
            return this.$options.filters.date(service.doneDate);
          },
        },
        {
          value: 'device',
          text: i18n.t('device.singular'),
          getValue: (service) => {
            if (service.defectReport?.writeInDeviceName) {
              return service.defectReport.writeInDeviceName;
            }
            if (service.device != null) {
              return this.$options.filters.device(service.device);
            }
            return null;
          },
        },
        {
          value: 'plannedService',
          text: i18n.t('plannedService.singular'),
          getValue: (service) => {
            return service.plannedServiceInstance?.name;
          },
        },
        {
          value: 'defectReport',
          text: i18n.t('defectReport.singular'),
          getValue: (service) => {
            return service.defectReport?.title;
          },
        },
        {
          value: 'durationMinutes',
          text: i18n.t('service.duration'),
          getValue: (service) => {
            return this.$options.filters.duration(service.durationMinutes);
          },
        },
        {
          value: 'doneByUsers',
          text: i18n.t('service.doneByUsers'),
          getValue: (service) => {
            return service.doneByUsers.map((doneByUser) =>
              this.$options.filters.user(doneByUser.worker),
            );
          },
        },
        {
          value: 'cleanedCheckedAccepted',
          text: i18n.t('service.cleanedCheckedAccepted'),
          getValue: (service) => {
            return this.$options.filters.yesNo(service.cleanedCheckedAccepted);
          },
        },
        {
          value: 'serviceState',
          text: i18n.t('service.serviceState'),
          getValue: (service) => {
            return this.$options.filters.serviceState(service.serviceState);
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: (service) => {
            return this.createRowActions(service);
          },
        },
      ];
    },
    tableRows() {
      return this.services.map((service) => {
        let row = {};
        row.service = service;
        row.routerLinkTo = `services/${service.id}`;

        row.rowClick = () => {
          this.$router.push(`services/${service.id}`);
        };

        this.tableHeaders.forEach((header) => {
          if (header.getValue) {
            row[header.value] = header.getValue(service);
          }
        });
        return row;
      });
    },

    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.serviceCRUD,
          click: () => {
            this.confirmDelete(this.selectedRows);
          },
        },
      ];
      return actions.filter((action) => action.hasPermission);
    },
  },
  methods: {
    createRowActions(service) {
      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: true,
          click: () => {
            this.$router.push(`services/${service.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.serviceCRUD,
          click: () => {
            this.openEditDialog(service.id);
          },
        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.serviceCRUD,
          click: () => {
            this.confirmDelete([{ service: service }]);
          },
        },
      ];

      return actions.filter((action) => action.hasPermission);
    },

    confirmDelete(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deleteServices(items) {
      const serviceIds = items.map((item) => item.service.id);
      ServiceService.delete(serviceIds).finally(() => {
        this.$refs.confirmDeleteDialog.close();
        this.fetch();
      });
    },

    openEditDialog(serviceId) {
      this.$refs.serviceDialog.openEditService(serviceId);
    },

    openQuickPartUseServiceDialog() {
      this.$refs.quickPartUseServiceDialog.openCreateQuickPartUseService();
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      ServiceService.list({
        deviceId: this.filterDevice?.id,
        dateRangeStart: this.dateRange.startDate,
        dateRangeEnd: this.dateRange.endDate,
        filterText: this.filterText,
        filterUserId: this.filterDoneByUser?.id,
        limit: this.tableOptions.itemsPerPage,
        offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        orderBy: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
      })
        .then((result) => {
          this.loading = false;
          this.services = result.data.items;
          this.totalCount = result.data.totalCount;
        })
        .catch((result) => {
          console.log(JSON.stringify(result));
          this.loading = false;
        });
    },
  },

  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
